import styled from 'styled-components';

import DonutChart from 'components/shared/charts/DonutChart';
import CircularProgress from 'components/shared/CircularProgress';
import { BodySmall } from 'styles/typography';

import { INBOUND_STATUS_COLORS, INBOUND_STATUS_LABELS, INBOUND_STATUSES, InboundStatus } from '../constants';

type Props = {
  statusCounts: Record<InboundStatus, number>;
  totalReferrals: number;
  loading: boolean;
};

export default function InboundReferralStatusDonutChart({ statusCounts, totalReferrals, loading }: Props) {
  if (loading) {
    return (
      <ChartContainer>
        <StyledProgress />
      </ChartContainer>
    );
  }

  const chartData = INBOUND_STATUSES.map((status) => ({
    name: INBOUND_STATUS_LABELS[status],
    y: statusCounts[status] ?? 0,
    color: INBOUND_STATUS_COLORS[status],
  }));

  return (
    <ChartContainer>
      <ChartLegendRow>
        <DonutChart
          data={chartData}
          centerValue={totalReferrals}
          centerLabel='Total'
          config={{
            chart: { height: 200, width: 200 },
            title: { y: 14 },
            legend: { enabled: false },
            plotOptions: {
              pie: {
                size: 200,
                innerSize: '50%',
                center: ['50%', '50%'],
              },
            },
          }}
        />
        <Legend>
          {chartData.map((item) => {
            const pct = totalReferrals > 0 ? Math.round((item.y / totalReferrals) * 100) : 0;
            return (
              <LegendItem key={item.name}>
                <LegendSwatch $color={item.color} />
                <LegendLabel>{item.name}</LegendLabel>
                <LegendValue>
                  {item.y} <LegendPct>({pct}%)</LegendPct>
                </LegendValue>
              </LegendItem>
            );
          })}
        </Legend>
      </ChartLegendRow>
    </ChartContainer>
  );
}

const ChartContainer = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;

  @media (max-width: 1200px) {
    padding: 24px 16px;
  }
`;

const ChartLegendRow = styled.div`
  display: flex;
  align-items: center;

  > :first-child {
    flex: 0 0 200px;
    padding-right: clamp(0px, 3vw, 20px);
  }

  @media (max-width: 1200px) {
    flex-direction: column;
    gap: 16px;
    align-items: center;

    /* Highcharts injects inline flex:1 1 0 and overflow:hidden on its wrapper, which collapse
       to 0 height in a column-direction parent and clip the chart — override both. */
    > :first-child {
      flex: none !important;
      width: 200px;
      height: 200px;
      overflow: visible !important;
      padding-right: 0;
    }
  }
`;

const Legend = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-left: clamp(0px, 3vw, 20px);

  @media (max-width: 1200px) {
    padding-left: 0;
  }

  @media (max-width: 800px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px 24px;
  }
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LegendSwatch = styled.div<{ $color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 2px;
  background-color: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const LegendLabel = styled(BodySmall)`
  flex: 1;
  color: ${({ theme }) => theme.colors.black75};
`;

const LegendValue = styled(BodySmall)`
  white-space: nowrap;
  font-weight: var(--font-weight-medium);
  color: ${({ theme }) => theme.colors.black100};
`;

const LegendPct = styled.span`
  font-weight: var(--font-weight-regular);
  color: ${({ theme }) => theme.colors.black50};
`;

const StyledProgress = styled(CircularProgress)`
  color: ${({ theme }) => theme.colors.blue100};
`;
